import { ISmartCanvasLayer, ISmartCanvasOptions } from '../../../shared/models/interfaces/smart-canvas.interface';
import { IFileDocument, IFilePageAndWords, IFilePageDetails } from '../../../shared/models/interfaces/files.interface';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { forkJoin } from 'rxjs';
import { IPageDimensions } from '../../../shared/models/interfaces/page-dimensions.interface';
import { GroupConfiguration } from '../../../shared/models/group-keys.model';
import * as pdfjsLib from 'pdfjs-dist';
import { EventEmitterService } from 'src/modules/shared/services/event-emitter.service';
import { ClassificationParameters, RobotPreview } from 'src/modules/shared/models/robots.model';

declare const Konva: any;

@Component({
  selector: 'hcr-smart-canvas',
  templateUrl: './smart-canvas.component.html',
  styleUrls: ['./smart-canvas.component.sass'],
})
export class SmartCanvasComponent implements OnInit, OnChanges {
  @Input() selectedFile: IFileDocument;
  @Input() selectedRobot: ClassificationParameters;
  @Input() selectedFilePagesAndWords: IFilePageAndWords[];
  @Input() selectedRobotFilePreview: RobotPreview;
  @Input() selectedGroupConfiguration: GroupConfiguration;
  @Input() smartCanvasOptions: ISmartCanvasOptions;
  @ViewChild('fileCanvasContainer', { static: true }) fileCanvasContainerElem: ElementRef;
  @ViewChild('fileCanvasInnerContainer', { static: true }) fileCanvasInnerContainerElem: ElementRef;
  @Output() canvasFilesLoaded = new EventEmitter<boolean>();

  public pagesToShow: number[] = [];
  public konvaStage: any; //Konva.Stage;
  public stagePageSizes: IPageDimensions[] = [];
  public selectedFileNumberOfPages: number;

  private _viewportScale = 1.3;
  private _pdfLoaded: pdfjsLib.PDFDocumentProxy = null;
  private _originalPagesToShow: number[] = [];

  public layers: { [key: string]: ISmartCanvasLayer } = {
    fileImages: { konvaLayer: new Konva.Layer(), polygons: [] },
    fileDataWords: { konvaLayer: new Konva.Layer(), polygons: [] },
    tools: { konvaLayer: new Konva.Layer(), polygons: [] },
    fileTables: { konvaLayer: new Konva.Layer(), polygons: [] },
    landmarks: { konvaLayer: new Konva.Layer(), polygons: [] },
  };

  public states = {
    errorOnRendering: false,
    splitWords: false,
  };

  @HostListener('window:resize', ['$event'])
  public onResize(): void {
    this._resize();
  }

  constructor(private _eventEmitterService: EventEmitterService) {
    const pdfWorkerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

    pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;
  }

  private _getImagesFromPDF(pdf: pdfjsLib.PDFDocumentProxy): Promise<IFilePageDetails[]> {
    return new Promise((resolve, reject) => {
      const numberOfPages = pdf.numPages;
      const pagesSubscriptions: Promise<pdfjsLib.PDFPageProxy>[] = [];

      for (let i = 0; i < this.pagesToShow.length; i++) {
        if (this.pagesToShow[i] <= numberOfPages) {
          pagesSubscriptions.push(pdf.getPage(this.pagesToShow[i]));
        }
      }

      const sub = forkJoin(pagesSubscriptions);

      sub.subscribe((pages) => {
        const renderSubscriptions: Promise<IFilePageDetails>[] = [];

        pages.forEach((page) => {
          const viewport = page.getViewport({ scale: this._viewportScale });
          const canvasElement: any = document.createElement('CANVAS');
          const canvasContext = canvasElement.getContext('2d');
          const pageDimensions: IPageDimensions = {
            height: viewport.height,
            width: viewport.width,
            orientation: null,
            unit: 'pixels',
          };

          canvasElement.height = viewport.height;
          canvasElement.width = viewport.width;

          const task = new Promise<IFilePageDetails>((resolveTask, rejectTask) => {
            page.render({ canvasContext, viewport, intent: 'print' }).promise.then(() => {
              const fileImageResult: IFilePageDetails = {
                url: canvasElement.toDataURL('image/jpeg'),
                pageNumber: page.pageNumber,
                pageDimensions: pageDimensions,
                totalPages: numberOfPages,
              };

              resolveTask(fileImageResult);
            }, rejectTask);
          });

          renderSubscriptions.push(task);
        });

        resolve(forkJoin<IFilePageDetails>(...renderSubscriptions).toPromise());
      }, reject);
      this.selectedFileNumberOfPages = numberOfPages;
    });
  }

  private _createCanvasStage(): void {
    this.konvaStage = new Konva.Stage({
      container: this.fileCanvasInnerContainerElem.nativeElement,
      width: this.fileCanvasContainerElem.nativeElement.clientWidth,
      height: this.fileCanvasContainerElem.nativeElement.clientHeight,
      draggable: true,
    });

    // Scroll
    this.konvaStage.on('wheel', (event) => {
      const oldScale = this.konvaStage.scaleX();

      const mousePointTo = {
        x: this.konvaStage.getPointerPosition().x / oldScale - this.konvaStage.x() / oldScale,
        y: this.konvaStage.getPointerPosition().y / oldScale - this.konvaStage.y() / oldScale,
      };

      const newScale = event.evt.deltaY < 0 ? oldScale * 1.1 : oldScale / 1.1;
      this.konvaStage.scale({ x: newScale, y: newScale });

      const newPos = {
        x: -(mousePointTo.x - this.konvaStage.getPointerPosition().x / newScale) * newScale,
        y: -(mousePointTo.y - this.konvaStage.getPointerPosition().y / newScale) * newScale,
      };

      this.konvaStage.position(newPos);
    });

    /// Drag with RIGHT mouse button
    if (this.smartCanvasOptions.enableAreaSelection) {
      Konva.dragButtons = [1, 2];
    } else {
      Konva.dragButtons = [0];
    }

    this.konvaStage.on('contextmenu', (e) => {
      e.evt.preventDefault();
    });
  }

  private _renderCanvasStage(): void {
    this.states.errorOnRendering = false;
    this.stagePageSizes = [];
    this.layers.fileImages.konvaLayer.removeChildren();

    if (!this.selectedFile || this.pagesToShow.length === 0) {
      return;
    }

    if (this.selectedFile.fileType === 'pdf' || this.selectedFile.fileExtension === 'pdf') {
      if (this._pdfLoaded) {
        this._getImagesFromPDF(this._pdfLoaded)
          .then((imagesFromPDF) => {
            this._renderImageOnCanvas(imagesFromPDF);
          })
          .catch((error) => {
            console.error(error);
            this.states.errorOnRendering = true;
          });

        return;
      }

      pdfjsLib
        .getDocument({
          url: this.selectedFile.fileURI,
          disableAutoFetch: true,
          disableStream: false,
        })
        .promise.then((pdf) => {
          this._pdfLoaded = pdf;
          this._getImagesFromPDF(pdf)
            .then((imagesFromPDF) => {
              this._renderImageOnCanvas(imagesFromPDF);
            })
            .catch((error) => {
              console.error(error);
              this.states.errorOnRendering = true;
            });
        })
        .catch((error) => {
          console.error(error);
          this.states.errorOnRendering = true;
        });
    } else if (
      this.selectedFile.fileType === 'image' ||
      this.selectedFile.fileExtension === 'png' ||
      this.selectedFile.fileExtension === 'jpg' ||
      this.selectedFile.fileExtension === 'bmp' ||
      this.selectedFile.fileExtension === 'jpeg' ||
      this.selectedFile.fileExtension === 'jfif'
    ) {
      this._renderImageOnCanvas([
        {
          url: this.selectedFile.fileURI,
          pageDimensions: null,
          pageNumber: 1,
          totalPages: 1,
        },
      ]);
    }
  }

  private _getPageXPosition(imageElement: any, pageOnCanvasIndex: number) {
    return imageElement.width * pageOnCanvasIndex + this.smartCanvasOptions.pageGutter * pageOnCanvasIndex + (pageOnCanvasIndex === 0 ?  this.smartCanvasOptions.customStyles.fileSpacing : 0);
  }

  private _renderImageOnCanvas(images: IFilePageDetails[]) {
    images.forEach((imageData, pageOnCanvasIndex) => {
      Konva.Image.fromURL(imageData.url, (darthNode: any /*Konva.Shape*/) => {
        const imageElement = darthNode.attrs.image;
        const readedPages = images.length;

        this.stagePageSizes.push({
          height: imageElement.height,
          width: imageElement.width,
          orientation: 0,
          unit: 'pixels',
        });

        const maxWidthSize = imageElement.width * readedPages + this.smartCanvasOptions.pageGutter * (readedPages - 1);

        // Set Stage size
        if (pageOnCanvasIndex === 0) {
          const xScale = this.fileCanvasContainerElem.nativeElement.clientWidth / maxWidthSize;
          const yScale = this.fileCanvasContainerElem.nativeElement.clientHeight / imageElement.height;
          const scale = xScale > yScale ? yScale : xScale;

          this.konvaStage.scaleX(scale);
          this.konvaStage.scaleY(scale);
          this.konvaStage.position({
            x: this.fileCanvasContainerElem.nativeElement.clientWidth / 2,
            y: this.fileCanvasContainerElem.nativeElement.clientHeight / 2,
          });
          this.konvaStage.offsetX(maxWidthSize / 2);
          this.konvaStage.offsetY(imageElement.height / 2);
        }

        darthNode.setAttrs({
          x: this._getPageXPosition(imageElement, pageOnCanvasIndex),
          y: this.smartCanvasOptions.customStyles.fileSpacing,
          width: imageElement.width - this.smartCanvasOptions.customStyles.fileSpacing,
          height: imageElement.height - this.smartCanvasOptions.customStyles.fileSpacing * 2,
          pageNumber: imageData.pageNumber,
          pageIndex: pageOnCanvasIndex,
        });

        this.layers.fileImages.konvaLayer.add(darthNode);

        if (this._pdfLoaded && this._pdfLoaded.numPages > this.smartCanvasOptions.maxPagesToRead) {
          const pageNumberLabel = new Konva.Label({
            x: darthNode.x() + darthNode.width() - 30,
            y: darthNode.y() + darthNode.height() - 30,
          });

          pageNumberLabel.add(
            new Konva.Circle({
              fill: '#f8f9fa',
              shadowColor: '#212529',
              shadowOffset: { x: 1, y: 1 },
              shadowOpacity: 0.1,
              shadowBlur: 2,
              radius: 20,
            })
          );

          pageNumberLabel.add(
            new Konva.Text({
              text: imageData.pageNumber,
              verticalAlign: 'middle',
              align: 'center',
              fontSize: 18,
              width: 40,
              height: 40,
              x: -20,
              y: -18,
            })
          );

          this.layers.fileImages.konvaLayer.add(pageNumberLabel);
        }

        if (pageOnCanvasIndex === readedPages - 1) {
          this.stagePageSizes = [...this.stagePageSizes];
        }
      });
    });

    this._resize();
    this.canvasFilesLoaded.emit(true);
  }

  private _resize(): void {
    if (this.konvaStage) {
      this.konvaStage.width(this.fileCanvasContainerElem.nativeElement.clientWidth);
      this.konvaStage.height(this.fileCanvasContainerElem.nativeElement.clientHeight);
    }
  }

  public onPagesToShowChange(pagesToShow: number[]): void {
    if (pagesToShow.length) {
      this.pagesToShow = pagesToShow;
      this._renderCanvasStage();
    }
  }

  ngOnInit(): void {
    this._createCanvasStage();

    Object.values(this.layers).forEach((layer) => {
      this.konvaStage.add(layer.konvaLayer);
    });

    this._eventEmitterService.get('smart-canvas-resize').subscribe(() => this._resize());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedFile) {
      this._pdfLoaded = null;
      this.onPagesToShowChange(this._originalPagesToShow);
    }

    if (changes.smartCanvasOptions?.currentValue) {
      this._originalPagesToShow = new Array(this.smartCanvasOptions.maxPagesToRead).fill(1).map((_, i) => i + 1);
      this.onPagesToShowChange(this._originalPagesToShow);
    }
  }
}
